import React, { useCallback, useState } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { endOfDay, startOfDay, format } from 'date-fns'
import {
  useInsuranceErrors,
  useInsuranceErrorsLazy,
  InsuranceErrors_getInsuranceErrors_insurances,
  InsuranceErrors,
} from '@modmd/data'
import papa from 'papaparse'
import DashboardPage from 'internal-portal/sharedComponents/DashboardPage'
import Datalist from 'components/Datalist'
import Card from 'components/Card'
import { Box, Container } from 'components/Layout'
import { useSearchState } from 'internal-portal/utils/useSearchState'
import { DateInput } from 'components/DateInput'
import { InsuranceErrorsDataList } from 'sharedComponents/InsuranceErrorsDatalist'
import Button from 'components/Button'
import { Spinner } from 'components/Spinner'
import { useToaster } from 'utils/useToaster'

export const USERS_PAGE_LENGTH = 10

export enum FormName {
  DATE_FROM = 'dateFrom',
  DATE_TO = 'dateTo',
}

const InsuranceErrorsPage: React.VFC<RouteComponentProps> = () => {
  const { setToastMessage } = useToaster()
  const today = new Date()
  const lastWeek = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7)
  const searchState = useSearchState<{
    [FormName.DATE_TO]?: string | null
    [FormName.DATE_FROM]?: string | null
  }>()
  const [download, setDownload] = useState(false)

  const generateCsv = (data: object[]) => {
    const csv = papa.unparse(data, {
      header: true,
    })

    return new Blob([csv], { type: 'text/csv' })
  }

  const createDownloadableLink = (blob: Blob, fileName: string) => {
    const link = document.createElement('a')
    link.href = window.URL.createObjectURL(blob)
    link.setAttribute('download', `${fileName}.csv`)
    document.body.appendChild(link)

    const click = () => {
      link.click()
      document.body.removeChild(link)
    }

    return { link, click }
  }

  const [page, setPage] = React.useState(1)
  const { data: insuranceErrors, loading: loadingInsuranceErrors } = useInsuranceErrors({
    filter: {
      checkedBetween: [searchState.data.dateFrom as string, searchState.data.dateTo as string],
    },
    page,
  })

  const parseResultIntoCSVModel = (
    insurances: InsuranceErrors_getInsuranceErrors_insurances[] | null
  ) =>
    insurances?.map((insurance) => {
      const {
        id,
        User: { lastName, firstName, gender, birthDate },
        insuranceCompany,
        subscriberFirstName,
        subscriberLastName,
        subscriberDOB,
        plan,
        SSN,
        govId,
        eligibilityErrorMessage,
        lastEligibilityCheck,
        cardID,
      } = insurance

      return {
        ID: id,
        'Patient First Name': firstName || '',
        'Patient Last Name': lastName || '',
        'Patient DOB': birthDate,
        'Patient Gender': gender,
        'Insurance Company': insuranceCompany,
        Subscriber: `${subscriberFirstName || ''} ${subscriberLastName || ''}`,
        'Subscriber DOB': subscriberDOB,
        'Insurance Plan': plan,
        'Insurance Card ID': cardID || '',
        Ssn: SSN,
        'Gov. Id File': govId,
        Error: eligibilityErrorMessage,
        'Last Eligibility Check': lastEligibilityCheck,
      }
    })

  const handleDownloadCsv = (result: InsuranceErrors) => {
    if (
      insuranceErrors?.getInsuranceErrors.insurances &&
      insuranceErrors?.getInsuranceErrors.insurances.length < 1
    ) {
      setToastMessage('No results found', 'danger')

      return
    }

    const parsedResult = parseResultIntoCSVModel(result?.getInsuranceErrors.insurances)
    const dataBlob = generateCsv(parsedResult!)

    if (download)
      createDownloadableLink(
        dataBlob,
        `InsuranceErrors_From_${
          searchState.data.dateFrom
            ? `${format(new Date(searchState.data.dateFrom), 'MM-dd-yyyy')}`
            : `${format(new Date(lastWeek), 'MM-dd-yyyy')}`
        }_To_${
          searchState.data.dateTo
            ? `${format(new Date(searchState.data.dateTo), 'MM-dd-yyyy')}`
            : `${format(new Date(today), 'MM-dd-yyyy')}`
        }`
      ).click()
    setDownload(false)
  }

  const [getInsuranceErrors, { loading: lazyInsuranceErrorsLoading }] = useInsuranceErrorsLazy({
    onCompleted: handleDownloadCsv,
  })

  const handleGetInsuranceErrors = useCallback(() => {
    getInsuranceErrors({
      variables: {
        filterData: {
          checkedBetween: [searchState.data.dateFrom as string, searchState.data.dateTo as string],
        },
        pagination: {
          from: 0,
          length: insuranceErrors?.getInsuranceErrors?.pagination?.total as number,
        },
      },
    })
  }, [searchState, getInsuranceErrors, insuranceErrors])

  return (
    <DashboardPage>
      <Container>
        <Card>
          <InsuranceErrorsDataList
            tableHeader={
              <Datalist.Title
                rightContent={
                  <>
                    <Box
                      display="grid"
                      gridGap="1rem"
                      alignItems="center"
                      gridTemplateColumns={{
                        TABLET: 'repeat(3, 1fr)',
                        DESKTOP: '0.7fr 0.7fr 0.7fr 0.35fr',
                      }}
                    >
                      <DateInput
                        id="checkedFrom"
                        name="checkedFrom"
                        label="Checked Date From"
                        maxDate={
                          searchState.data.dateTo ? new Date(searchState.data.dateTo) : new Date()
                        }
                        value={
                          searchState.data.dateFrom ? new Date(searchState.data.dateFrom) : lastWeek
                        }
                        onChange={(date) => {
                          searchState.setSearchState(
                            {
                              [FormName.DATE_FROM]: date ? startOfDay(date).toISOString() : null,
                              page: 1,
                            },
                            false,
                            true
                          )
                        }}
                        hasBottomContent
                        isFullWidth
                      />
                      <DateInput
                        id="checkedTo"
                        name="checkedTo"
                        label="Checked Date To"
                        minDate={
                          searchState.data.dateFrom ? new Date(searchState.data.dateFrom) : lastWeek
                        }
                        value={searchState.data.dateTo ? new Date(searchState.data.dateTo) : today}
                        onChange={(date) => {
                          searchState.setSearchState(
                            {
                              [FormName.DATE_TO]: date ? endOfDay(date).toISOString() : null,
                              page: 1,
                            },
                            false,
                            true
                          )
                        }}
                        hasBottomContent
                        isFullWidth
                      />
                      <Button
                        onClick={() => {
                          setDownload(true)
                          handleGetInsuranceErrors()
                        }}
                        isFetching={lazyInsuranceErrorsLoading && download}
                        colorVariant="primary"
                      >
                        {lazyInsuranceErrorsLoading && download ? (
                          <Spinner size="small" />
                        ) : (
                          `Download csv`
                        )}
                      </Button>
                    </Box>
                  </>
                }
              >
                List of insurance errors
              </Datalist.Title>
            }
            isLoading={loadingInsuranceErrors}
            insuranceErrors={insuranceErrors?.getInsuranceErrors.insurances || []}
            pagination={insuranceErrors?.getInsuranceErrors?.pagination}
            onPageChange={setPage}
          />
        </Card>
      </Container>
    </DashboardPage>
  )
}

export { InsuranceErrorsPage }
